/**
 * Google Pay / Apple Pay (via Pagar.me) — utilitários sem JSX.
 *
 * Contrato com o backend (CartController@finalize, payment_type 119/120):
 *   Google Pay: wallet_token = JSON.parse(paymentData.paymentMethodData.tokenizationData.token)
 *   Apple Pay:  wallet_token = event.payment.token.paymentData
 */

export const PAYMENT_TYPE_GOOGLE_PAY = 119;
export const PAYMENT_TYPE_APPLE_PAY = 120;

export const WALLET_TAGS = ['GooglePay', 'ApplePay'];

export const GOOGLE_PAY_ENV =
  process.env.REACT_APP_GOOGLE_PAY_ENV === 'PRODUCTION' ? 'PRODUCTION' : 'TEST';

export const GOOGLE_PAY_MERCHANT_ID =
  process.env.REACT_APP_GOOGLE_PAY_MERCHANT_ID || '';

const GOOGLE_PAY_SCRIPT = 'https://pay.google.com/gp/p/js/pay.js';

// Bandeiras compatíveis com a Stone (doc Pagar.me): Visa e Mastercard.
const BASE_CARD_PAYMENT_METHOD = {
  type: 'CARD',
  parameters: {
    allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
    allowedCardNetworks: ['MASTERCARD', 'VISA'],
  },
};

const scriptPromises = {};

function loadScript(src) {
  if (scriptPromises[src]) return scriptPromises[src];

  scriptPromises[src] = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing && existing.dataset.loaded === 'true') {
      resolve();
      return;
    }

    const script = existing || document.createElement('script');
    script.addEventListener('load', () => {
      script.dataset.loaded = 'true';
      resolve();
    });
    script.addEventListener('error', () => {
      delete scriptPromises[src];
      reject(new Error(`Não foi possível carregar ${src}`));
    });

    if (!existing) {
      script.src = src;
      script.async = true;
      document.head.appendChild(script);
    }
  });

  return scriptPromises[src];
}

export function formatWalletAmount(amount) {
  return Number(amount || 0).toFixed(2);
}

/** Erro lançado quando o cliente fecha a sheet sem pagar. */
export function isWalletCanceled(err) {
  return (
    !!err &&
    (err.canceled === true ||
      err.statusCode === 'CANCELED' ||
      err.message === 'CANCELED')
  );
}

/* ----------------------------- Google Pay ----------------------------- */

let paymentsClient = null;

export async function getGooglePaymentsClient() {
  await loadScript(GOOGLE_PAY_SCRIPT);

  if (!window.google?.payments?.api?.PaymentsClient) {
    throw new Error('Google Pay indisponível neste navegador');
  }

  if (!paymentsClient) {
    paymentsClient = new window.google.payments.api.PaymentsClient({
      environment: GOOGLE_PAY_ENV,
    });
  }

  return paymentsClient;
}

export async function isGooglePayReady() {
  try {
    const client = await getGooglePaymentsClient();
    const response = await client.isReadyToPay({
      apiVersion: 2,
      apiVersionMinor: 0,
      allowedPaymentMethods: [BASE_CARD_PAYMENT_METHOD],
    });
    return !!response?.result;
  } catch (err) {
    console.warn('[GooglePay] isReadyToPay falhou:', err?.message || err);
    return false;
  }
}

/**
 * Abre a sheet do Google Pay e devolve o token (objeto) para o wallet_token.
 * Rejeita com { canceled: true } quando o cliente fecha a sheet.
 */
export async function requestGooglePayToken({
  gatewayMerchantId,
  merchantName,
  amount,
}) {
  const client = await getGooglePaymentsClient();

  const merchantInfo = { merchantName: String(merchantName || 'Loja') };
  if (GOOGLE_PAY_MERCHANT_ID) merchantInfo.merchantId = GOOGLE_PAY_MERCHANT_ID;

  const request = {
    apiVersion: 2,
    apiVersionMinor: 0,
    allowedPaymentMethods: [
      {
        ...BASE_CARD_PAYMENT_METHOD,
        tokenizationSpecification: {
          type: 'PAYMENT_GATEWAY',
          parameters: {
            gateway: 'pagarme',
            gatewayMerchantId,
          },
        },
      },
    ],
    merchantInfo,
    transactionInfo: {
      totalPriceStatus: 'FINAL',
      totalPrice: formatWalletAmount(amount),
      currencyCode: 'BRL',
      countryCode: 'BR',
    },
  };

  let paymentData;
  try {
    paymentData = await client.loadPaymentData(request);
  } catch (err) {
    if (err?.statusCode === 'CANCELED') {
      const canceled = new Error('CANCELED');
      canceled.canceled = true;
      throw canceled;
    }
    throw err;
  }

  const tokenString = paymentData?.paymentMethodData?.tokenizationData?.token;
  if (!tokenString) throw new Error('Token do Google Pay não retornado');

  return JSON.parse(tokenString);
}

/* ------------------------------ Apple Pay ----------------------------- */

export function isApplePayBrowser() {
  try {
    return (
      typeof window !== 'undefined' &&
      !!window.ApplePaySession &&
      window.ApplePaySession.canMakePayments()
    );
  } catch (err) {
    return false;
  }
}

/**
 * Abre a sheet do Apple Pay e devolve paymentData (objeto) para o wallet_token.
 * onValidateMerchant(validationURL) deve chamar o backend e devolver a merchant session.
 * Rejeita com { canceled: true } quando o cliente fecha a sheet.
 *
 * A sheet é fechada com STATUS_SUCCESS assim que o token é emitido; a autorização
 * na Pagar.me acontece em seguida no finalize. Recusa é tratada pelo checkout.
 */
export function requestApplePayToken({
  merchantName,
  amount,
  onValidateMerchant,
}) {
  return new Promise((resolve, reject) => {
    const { ApplePaySession } = window;

    const request = {
      countryCode: 'BR',
      currencyCode: 'BRL',
      supportedNetworks: ['visa', 'masterCard'],
      merchantCapabilities: ['supports3DS'],
      total: {
        label: String(merchantName || 'Loja').slice(0, 64),
        amount: formatWalletAmount(amount),
      },
    };

    let session;
    try {
      session = new ApplePaySession(3, request);
    } catch (err) {
      reject(err);
      return;
    }

    let settled = false;

    session.onvalidatemerchant = async event => {
      try {
        const merchantSession = await onValidateMerchant(event.validationURL);
        session.completeMerchantValidation(merchantSession);
      } catch (err) {
        settled = true;
        try {
          session.abort();
        } catch (abortErr) {
          // ignore
        }
        reject(err);
      }
    };

    session.onpaymentauthorized = event => {
      const paymentData = event?.payment?.token?.paymentData;

      if (!paymentData) {
        settled = true;
        session.completePayment(ApplePaySession.STATUS_FAILURE);
        reject(new Error('Token do Apple Pay não retornado'));
        return;
      }

      settled = true;
      session.completePayment(ApplePaySession.STATUS_SUCCESS);
      resolve(paymentData);
    };

    session.oncancel = () => {
      if (settled) return;
      const canceled = new Error('CANCELED');
      canceled.canceled = true;
      reject(canceled);
    };

    session.begin();
  });
}
